import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  CButton,
  CSpinner,
  CAlert,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilMediaPlay,
  // cilClock,
  cilTrash,
  cilZoom,
  cilImage,
  cilMovie,
  cilSpeech,
} from "@coreui/icons";
import MainNeuralModal from "../../NeuralNetworkModule/MainNeuralModal";
import FileUploader from "./FileUploader";
import axios from "axios";
import { toast } from "react-toastify";

// Вспомогательный компонент для миниатюр
const MediaItem = React.memo(({ file, token, ldoc, onDelete, onOpen }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  const isVideo = file.name
    .split(".")
    .pop()
    .toLowerCase()
    .match(/(mp4|mov|avi|m4v)/);
  const srcRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    if (isVideo) {
      setLoading(false);
      return;
    }

    // Загрузка превью
    axios
      .get(
        `/api/ldoc/${ldoc}/workdir/preview/?path=${encodeURIComponent(file.path)}&width=100&height=100&quality=70`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      )
      .then((res) => {
        if (isMounted) {
          const url = URL.createObjectURL(new Blob([res.data]));
          srcRef.current = url;
          setSrc(url);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
      if (srcRef.current) URL.revokeObjectURL(srcRef.current);
    };
  }, [file.path, ldoc, token, isVideo]);

  return (
    <div className="thumb-wrapper">
      <div className="thumb-content" onClick={() => onOpen(file)}>
        {loading ? (
          <CSpinner size="sm" color="secondary" />
        ) : isVideo ? (
          <div className="video-placeholder">
            <CIcon icon={cilMovie} size="xl" />
          </div>
        ) : src ? (
          <img src={src} alt="" className="thumb-img" />
        ) : (
          <CIcon icon={cilImage} size="xl" className="text-muted" />
        )}
      </div>

      <div className="thumb-controls">
        <button
          className="control-btn zoom"
          onClick={() => onOpen(file)}
          title="Увеличить"
        >
          <CIcon icon={cilZoom} />
        </button>
        <button
          className="control-btn delete"
          onClick={(e) => onDelete(e, file)}
          title="Удалить и скрыть из обработки"
        >
          <CIcon icon={cilTrash} />
        </button>
      </div>
      <div className="thumb-name" title={file.name}>
        {file.name}
      </div>
    </div>
  );
});
const usePolling = (callback, delay, enabled = true) => {
  const savedCallback = useRef();
  const timerRef = useRef(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || delay === null) return;

    let isMounted = true;

    const tick = async () => {
      if (!isMounted) return;
      try {
        await savedCallback.current?.();
      } finally {
        if (isMounted) {
          timerRef.current = setTimeout(tick, delay);
        }
      }
    };

    timerRef.current = setTimeout(tick, delay);

    return () => {
      isMounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [delay, enabled]);
};

const NeuralModuleTab = ({ object }) => {
  const [isServiceAvailable, setIsServiceAvailable] = useState(false);
  const [serviceChecking, setServiceChecking] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [objectId, setObjectId] = useState(null);
  const [objectData, setObjectData] = useState(null);

  // Состояния процесса
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkingPhotos, setCheckingPhotos] = useState(false);
  const [inQueue, setInQueue] = useState(false);
  const [progress, setProgress] = useState("");
  const [mediaLoading, setMediaLoading] = useState(false);
  const [isPollingTask, setIsPollingTask] = useState(false);

  // Сообщения
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [noPhotosMessage, setNoPhotosMessage] = useState(false);

  // Задача
  const [, setCurrentTaskId] = useState(null);

  // Медиа
  const [mediaFiles, setMediaFiles] = useState([]);
  const [activeMediaTab, setActiveMediaTab] = useState(1);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);

  const [showNeuralModal, setShowNeuralModal] = useState(false);
  const [manualProcessingStarted, setManualProcessingStarted] = useState(false);
  const [queueStartTime, setQueueStartTime] = useState(null);
  const [showReprocessModal, setShowReprocessModal] = useState(false);

  const isProcessingRef = useRef(false);
  const token = localStorage.getItem("token");

  usePolling(
    async () => {
      try {
        const response = await fetch(
          "/api/neural_network_module/service-status/",
        );
        if (response.ok) {
          const data = await response.json();
          setIsServiceAvailable(data.status === "available");
        } else {
          setIsServiceAvailable(false);
        }
      } catch {
        setIsServiceAvailable(false);
      } finally {
        setServiceChecking(false);
      }
    },
    5000,
    true,
  );

  // Опрос задачи
  // Опрос задачи
  usePolling(
    async () => {
      const id = objectId || object?.LineListID;
      if (!id) return;
      try {
        const response = await fetch(
          `/api/neural_network_module/task-progress/${id}`,
        );
        if (response.ok) {
          const data = await response.json();
          console.log("Прогресс задачи:", {
            raw: data,
            id: data?.id,
            status: data?.status,
            progress: data?.progress,
            statusType: typeof data?.status,
            progressType: typeof data?.progress,
          });

          analyzeTaskStatus(data);
        }
      } catch (err) {
        console.error("Ошибка опроса задачи:", err);
        setIsPollingTask(false);
        setInQueue(false);
        setProcessing(false);
      }
    },
    1500,
    isPollingTask,
  );
  // --- 1. ЗАГРУЗКА МЕДИА ---
  const fetchMediaFiles = useCallback(async () => {
    if (!object?.LDoc || !token) return [];
    setMediaLoading(true);
    try {
      const response = await axios.get(`/api/ldoc/${object.LDoc}/workdir/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const files = response.data || [];
      const filtered = files.filter((f) => {
        if (f.isDirectory) return false;
        if (f.path.toLowerCase().includes("thumbnails")) return false;
        const ext = f.name.split(".").pop().toLowerCase();
        return [
          "jpg",
          "jpeg",
          "png",
          "webp",
          "mp4",
          "mov",
          "avi",
          "m4v",
        ].includes(ext);
      });
      setMediaFiles(filtered);
      return filtered;
    } catch (err) {
      return [];
    } finally {
      setMediaLoading(false);
    }
  }, [object?.LDoc, token]);

  const handleFileDelete = useCallback(
    async (e, file) => {
      e.stopPropagation();
      if (!window.confirm(`Скрыть файл ${file.name}?`)) return;
      try {
        await axios.post(
          `/api/ldoc/${object.LDoc}/workdir/hidden/`,
          { path: file.path },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Файл скрыт");
        fetchMediaFiles();
      } catch {
        toast.error("Ошибка");
      }
    },
    [object?.LDoc, token, fetchMediaFiles],
  );

  const openFullPreview = useCallback(
    async (file) => {
      const isVideo = /\.(mp4|mov|avi|m4v)$/i.test(file.name);
      setSelectedPreview({ ...file, isVideo, url: null });
      setPreviewModalOpen(true);
      try {
        const response = await axios.get(
          `/api/ldoc/${object.LDoc}/workdir/preview/?path=${encodeURIComponent(file.path)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );
        setSelectedPreview((prev) => ({
          ...prev,
          url: URL.createObjectURL(new Blob([response.data])),
        }));
      } catch {
        setPreviewModalOpen(false);
      }
    },
    [object?.LDoc, token],
  );

  const getObjectData = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `/api/neural_network_module/object-data/?id=${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.ok) {
          const data = await response.json();
          setObjectData(data);
          return data;
        }
        return null;
      } catch {
        return null;
      }
    },
    [token],
  );

  const analyzeTaskStatus = (data) => {
    console.log("Финал прогресса задачи:", data);

    if (!data || !data.id) {
      return;
    }

    const { status, progress } = data;

    setCurrentTaskId(data.id);
    // setProgress(progress || "");
    const progStr = String(progress);

    // Нет активной задачи
    if (status === false && progStr === "-1") {
      if (manualProcessingStarted) {
        return;
      }
      setProcessing(false);
      setInQueue(false);
      setIsPollingTask(false);
      return;
    }

    //  В процессе
    if (status === true) {
      setProcessing(true);
      setInQueue(false);
      setProgress(progStr);
      return;
    }

    //  Завершена
    if (status === false && progStr !== "-1") {
      setProcessing(false);
      setInQueue(false); // Снимаем все блокировки
      setIsPollingTask(false);
      setManualProcessingStarted(false);

      // Обновляем список файлов (там должны появиться новые фото)
      fetchMediaFiles();

      // Показываем сообщение "Успешно", только если мы сами запускали процесс
      if (manualProcessingStarted) {
        setShowCompletionMessage(true);
        setTimeout(() => setShowCompletionMessage(false), 5000);
      }

      return;
    }
  };
  const createTask = async (taskType = null, hasVideo = false) => {
    const id = objectId || object?.LineListID;
    const data = objectData || (await getObjectData(id));
    const taskPath = data?.WorkDir
      ? data.WorkDir.trim().replace(/[\\/]+$/, "") + "\\Фото"
      : "";
    const taskData = {
      TaskPath: taskPath,
      TaskStatus: 0,
      UseGPS: 0,
      ObjectTypeID: data?.Id || 0,
      ExtObjectID: id,
      IsFullTask: !taskType || taskType === "full" ? 1 : 0,
      IsInFrames: hasVideo ? 1 : 0,
      IsNoCombined: taskType === "no-combined" ? 1 : 0,
    };
    const response = await fetch("/api/neural_network_module/task-ai/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(taskData),
    });
    return response.ok ? await response.json() : null;
  };

  const handleProcessObject = async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsActionInProgress(true);
    setNoPhotosMessage(false);

    // МГНОВЕННО БЛОКИРУЕМ UI
    setCheckingPhotos(true);
    setErrorMessage("");
    setShowCompletionMessage(false);
    setManualProcessingStarted(true);

    let shouldUnlock = true;

    try {
      if (!isServiceAvailable) {
        setErrorMessage("Сервис нейромодуля недоступен");
        resetProcessingState();
        return;
      }
      const id = objectId || object?.LineListID;
      if (!id) {
        setErrorMessage("ID объекта не найден");
        resetProcessingState();
        return;
      }

      const freshFiles = await fetchMediaFiles();
      const mediaFiles = freshFiles.filter((f) =>
        /\.(jpg|jpeg|png|webp|mp4|mov|avi|m4v)$/i.test(f.name),
      );
      const hasVideo = mediaFiles.some((f) =>
        /\.(mp4|mov|avi|m4v)$/i.test(f.name),
      );

      if (mediaFiles.length === 0) {
        setNoPhotosMessage(true);
        setTimeout(() => setNoPhotosMessage(false), 5000);
        return;
      }

      const checkTask = await axios
        .get(`/api/neural_network_module/task-ai/`, {
          params: { ext_object_id: id },
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => ({ data: {} }));
      const existingTaskId = checkTask.data?.task_id;

      if (existingTaskId) {
        setShowReprocessModal(true);
        setCheckingPhotos(false);
        setIsActionInProgress(false);
        isProcessingRef.current = false;
        return;
      }

      setInQueue(true);
      setQueueStartTime(Date.now());

      await createTask("full", hasVideo);
      setIsPollingTask(true);

      const updatedFiles = await fetchMediaFiles();
      setMediaFiles(updatedFiles);
    } catch (err) {
      console.error(err);
      setErrorMessage("Ошибка при запуске обработки");
      resetProcessingState();
    } finally {
      setCheckingPhotos(false);

      if (shouldUnlock) {
        setIsActionInProgress(false);
        isProcessingRef.current = false;
      }
    }
  };

  const handleTaskTypeSelect = async (type) => {
    setShowReprocessModal(false);
    setCheckingPhotos(false);

    isProcessingRef.current = false;
    setIsActionInProgress(false);

    setInQueue(true);
    setQueueStartTime(Date.now());

    const freshFiles = await fetchMediaFiles();
    const hasVideoNow = freshFiles.some((f) =>
      /\.(mp4|mov|avi|m4v)$/i.test(f.name),
    );

    try {
      await createTask(type, hasVideoNow);
      setIsPollingTask(true);
    } catch {
      setInQueue(false);
    }
  };

  const handleCancelReprocess = () => {
    setShowReprocessModal(false);
    setCheckingPhotos(false);
    setIsActionInProgress(false);
    isProcessingRef.current = false;
  };

  const resetProcessingState = useCallback(() => {
    setProcessing(false);
    setInQueue(false);
    setManualProcessingStarted(false);
    setCheckingPhotos(false);
    setShowReprocessModal(false);
    setIsPollingTask(false);
    setMediaLoading(false);
    isProcessingRef.current = false;
  }, []);
  useEffect(() => {
    const id = object?.LineListID;
    if (id) {
      setObjectId(id);
      getObjectData(id);
      fetch(`/api/neural_network_module/task-progress/${id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.id && data.status === true) setIsPollingTask(true);
        })
        .catch(() => {});
    }
    return () => {
      isProcessingRef.current = false;
      setIsPollingTask(false);
    };
  }, [object?.LineListID, getObjectData]);

  useEffect(() => {
    fetchMediaFiles();
    const handleUpdate = () => setTimeout(fetchMediaFiles, 1000);
    window.addEventListener("filesUploaded", handleUpdate);
    return () => window.removeEventListener("filesUploaded", handleUpdate);
  }, [fetchMediaFiles]);

  const parseProgress = (progressString) => {
    if (!progressString || progressString === "-1") return null;
    if (progressString.includes("/")) {
      const [current, total] = progressString.split("/").map(Number);
      if (isNaN(current)) return null;
      return {
        current,
        total,
        percentage: total > 0 ? Math.round((current / total) * 100) : 0,
      };
    }
    const num = Number(progressString);
    return !isNaN(num) ? { current: num, total: num, percentage: 100 } : null;
  };

  const progressData = parseProgress(progress);
  const hasFiles = useCallback(() => {
    return mediaFiles.some((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      return [
        "jpg",
        "jpeg",
        "png",
        "webp",
        "mp4",
        "mov",
        "avi",
        "m4v",
      ].includes(ext);
    });
  }, [mediaFiles]);

  const isButtonEnabled =
    isServiceAvailable &&
    !!object?.LDoc &&
    !serviceChecking &&
    !isActionInProgress &&
    !inQueue &&
    !processing &&
    !mediaLoading;

  const getButtonState = () => {
    if (serviceChecking) {
      return {
        text: "Проверка сервиса...",
        color: "secondary",
        disabled: true,
        icon: <CSpinner size="sm" className="me-2" />,
        show: true,
      };
    }

    if (showReprocessModal) {
      return {
        text: "Требуется подтверждение...",
        color: "warning",
        disabled: true,
        icon: <CSpinner size="sm" className="me-2" />,
        show: true,
      };
    }

    if (isActionInProgress || checkingPhotos) {
      return {
        text: checkingPhotos ? "Проверка файлов..." : "Подготовка...",
        color: "info",
        disabled: true,
        icon: <CSpinner size="sm" className="me-2" />,
        show: true,
      };
    }

    if (inQueue) {
      const wait = queueStartTime
        ? Math.floor((Date.now() - queueStartTime) / 1000)
        : 0;
      return {
        text: wait > 0 ? `В очереди... (${wait}с)` : "В очереди...",
        color: "warning",
        disabled: true,
        icon: <CSpinner size="sm" className="me-2" />,
        show: true,
      };
    }

    if (processing) {
      const progressText = progressData
        ? `Обработка... ${progressData.current}/${progressData.total}`
        : "Обработка...";
      return {
        text: progressText,
        color: "warning",
        disabled: true,
        icon: <CSpinner size="sm" className="me-2" />,
        show: true,
      };
    }

    if (!isServiceAvailable) {
      return {
        text: "Сервис недоступен",
        color: "secondary",
        disabled: true,
        icon: <CIcon icon={cilMediaPlay} className="me-2" />,
        show: true,
      };
    }

    return {
      text: "Обработать объект",
      color: "success",
      disabled: false,
      icon: <CIcon icon={cilMediaPlay} className="me-2" />,
      show: true,
    };
  };

  const buttonState = getButtonState();

  // Определяем, можно ли нажимать кнопку

  // Фильтрация для табов
  const photos = mediaFiles.filter(
    (f) =>
      !f.name
        .split(".")
        .pop()
        .toLowerCase()
        .match(/(mp4|mov|avi|m4v)/),
  );
  const videos = mediaFiles.filter((f) =>
    f.name
      .split(".")
      .pop()
      .toLowerCase()
      .match(/(mp4|mov|avi|m4v)/),
  );

  return (
    <div className="neural-module-tab">
      <div
        className="service-status-container"
        style={{ padding: "20px 0", textAlign: "center" }}
      >
        {/* Кнопка обработки объекта */}
        <CButton
          color={buttonState.color}
          disabled={!isButtonEnabled || buttonState.disabled}
          onClick={handleProcessObject}
          style={{
            minWidth: "250px",
            marginRight: "10px",
            transition: "all 0.2s ease",
          }}
        >
          {buttonState.icon}
          {buttonState.text}
        </CButton>

        {/* Кнопка нейросетевого модуля */}
        <CButton
          color="info"
          onClick={() => setShowNeuralModal(true)}
          style={{ minWidth: "250px" }}
          disabled={!object?.LDoc || serviceChecking}
        >
          Нейросетевой модуль
        </CButton>
         <CButton
          color="secondary"
          variant="outline"
          href="https://t.me/AppStructuraSupport"
          target="_blank"
          style={{ minWidth: "250px", marginLeft: "10px" }}
        >
          <CIcon icon={cilSpeech} className="me-2" />
          Поддержка в Telegram
        </CButton>
      </div>

      {object?.LDoc ? (
        <FileUploader ldoc={object.LDoc} token={token} />
      ) : (
        <div className="mb-4">
          <CAlert color="warning">Нет данных объекта</CAlert>
        </div>
      )}

      <div className="media-section mt-4 p-3 border rounded bg-white">
        <CTabs
          activeItemKey={activeMediaTab}
          onActiveItemChange={setActiveMediaTab}
        >
          <CTabList variant="tabs">
            <CTab itemKey={1}>
              <CIcon icon={cilImage} className="me-2" />
              Фото ({photos.length})
            </CTab>
            <CTab itemKey={2}>
              <CIcon icon={cilMovie} className="me-2" />
              Видео ({videos.length})
            </CTab>
          </CTabList>
          <CTabContent className="mt-3">
            <CTabPanel itemKey={1}>
              <div className="thumb-grid">
                {photos.length > 0 ? (
                  photos.map((f) => (
                    <MediaItem
                      key={f.path}
                      file={f}
                      token={token}
                      ldoc={object.LDoc}
                      onDelete={handleFileDelete}
                      onOpen={openFullPreview}
                    />
                  ))
                ) : (
                  <div className="no-media-message">
                    Нет фото для отображения
                  </div>
                )}
              </div>
            </CTabPanel>
            <CTabPanel itemKey={2}>
              <div className="thumb-grid">
                {videos.length > 0 ? (
                  videos.map((f) => (
                    <MediaItem
                      key={f.path}
                      file={f}
                      token={token}
                      ldoc={object.LDoc}
                      onDelete={handleFileDelete}
                      onOpen={openFullPreview}
                    />
                  ))
                ) : (
                  <div className="no-media-message">
                    Нет видео для отображения
                  </div>
                )}
              </div>
            </CTabPanel>
          </CTabContent>
        </CTabs>
      </div>

      <MainNeuralModal
        visible={showNeuralModal}
        onClose={() => setShowNeuralModal(false)}
        object={object}
        objectId={objectId}
        objectData={objectData}
      />

      {/* Модальное окно повторной обработки - ИСПРАВЛЕННЫЕ ПРОПСЫ */}
      <CModal
        className="modal__repeat"
        visible={showReprocessModal}
        backdrop="static"
        onClose={handleCancelReprocess}
      >
        <CModalHeader closeButton>
          <CModalTitle>Повторная обработка</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>Объект уже обрабатывался ранее! Выберите тип обработки:</p>
        </CModalBody>
        <CModalFooter>
          <CButton
            color="primary"
            onClick={() => handleTaskTypeSelect("no-combined")}
          >
            Без связок
          </CButton>
          <CButton color="primary" onClick={() => handleTaskTypeSelect("full")}>
            Полная
          </CButton>
          <CButton
            color="primary"
            onClick={() => handleTaskTypeSelect("partial")}
          >
            Частичная
          </CButton>
          <CButton color="secondary" onClick={handleCancelReprocess}>
            Отмена
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Модальное окно просмотра медиа */}
      <CModal
        visible={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        size="xl"
        alignment="center"
      >
        <CModalHeader closeButton>
          <CModalTitle className="small">{selectedPreview?.name}</CModalTitle>
        </CModalHeader>
        <CModalBody className="text-center bg-dark">
          {selectedPreview?.url ? (
            selectedPreview.isVideo ? (
              <video
                src={selectedPreview.url}
                controls
                autoPlay
                style={{ maxWidth: "100%", maxHeight: "75vh" }}
              />
            ) : (
              <img
                src={selectedPreview.url}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                }}
              />
            )
          ) : (
            <CSpinner color="light" />
          )}
        </CModalBody>
      </CModal>

      {/* Сообщения об ошибках и статусах */}
      {errorMessage && (
        <div className="mt-3">
          <CAlert color="danger">{errorMessage}</CAlert>
        </div>
      )}
      {showCompletionMessage && (
        <div className="mt-3">
          <CAlert color="success">Обработка завершена успешно</CAlert>
        </div>
      )}
      {noPhotosMessage && !hasFiles() && (
        <div className="mt-3">
          <CAlert color="warning">Нет медиафайлов для обработки</CAlert>
        </div>
      )}

      <style jsx>{`
        .thumb-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 12px;
          max-height: 400px;
          overflow-y: auto;
          padding: 5px;
        }
        .thumb-wrapper {
          position: relative;
          width: 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .thumb-content {
          width: 100px;
          height: 100px;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
        }
        .thumb-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .thumb-controls {
          position: absolute;
          bottom: 22px;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: space-around;
          padding: 2px 0;
          opacity: 0;
          transition: opacity 0.2s ease;
          border-radius: 0 0 6px 6px;
        }
        .thumb-wrapper:hover .thumb-controls {
          opacity: 1;
        }
        .control-btn {
          background: none;
          border: none;
          color: white;
          padding: 2px 5px;
          cursor: pointer;
        }
        .control-btn:hover {
          color: #007bff;
        }
        .control-btn.delete:hover {
          color: #dc3545;
        }
        .thumb-name {
          font-size: 9px;
          margin-top: 4px;
          width: 100%;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #666;
        }
        .video-placeholder {
          color: #dc3545;
        }

        /* Анимация для плавного перехода состояний */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .service-status-container button {
          animation: fadeIn 0.3s ease;
        }

        /* Стили для модалки повторной обработки */
        .modal__repeat .modal-dialog {
          max-width: 600px;
          margin: 1.75rem auto;
        }

        .no-media-message {
          grid-column: 1 / -1;
          text-align: center;
          padding: 20px;
          color: #6c757d;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default NeuralModuleTab;
